import { useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import TableBody from '@material-ui/core/TableBody';
import { useNavigate } from 'react-router';
import { Card, CardBody } from '@/shared/components/Card';
import { Table, TableRow, TableWrap, TablePagination, TableCell } from '@/shared/components/MaterialTableElements';
import MatTableHead from './components/MatTableHead';
import MatTableToolbar from './components/MatTableToolbar';
import { Schedule } from '@/models/schedule';
import { useSchedules } from '@/services/schedules';
import ScheduleModal from './components/schedule-modal';
import { LoadingSpinner } from '@/shared/components/Loading';
import { MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { formatDate } from '@/utils/helpers';

const valueFor = (s: Schedule, key: string): string | number => {
    switch (key) {
        case 'targetType':
            return s.target?.type ?? '';
        case 'targetTitle':
            return s.target?.title ?? '';
        case 'time':
            return s.publishAtHourUtc * 60 + s.publishAtMinuteUtc;
        case 'queueDepth':
            return s.queueDepth ?? 0;
        case 'enabled':
            return s.enabled ? 1 : 0;
        case 'nextPublishAt':
            return s.nextPublishAt ?? '';
        default: {
            const v = (s as unknown as Record<string, unknown>)[key];
            return (v as string | number) ?? '';
        }
    }
};

const getSorting = (order: 'asc' | 'desc', orderBy: string) => (a: Schedule, b: Schedule) => {
    const av = valueFor(a, orderBy);
    const bv = valueFor(b, orderBy);
    if (av < bv) return order === 'desc' ? 1 : -1;
    if (av > bv) return order === 'desc' ? -1 : 1;
    return 0;
};

const Schedules = () => {
    const navigate = useNavigate();
    const { data: schedules, isLoading } = useSchedules();
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [orderBy, setOrderBy] = useState('nextPublishAt');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    // Derive filtered list from raw data + search input rather than mirroring
    // into separate state — fixes the bug where a query-active search reset
    // to the full list whenever React Query refetched (e.g. after a mutation
    // invalidated the cache).
    const filtered = useMemo(() => {
        const data = schedules?.data ?? [];
        if (!searchQuery.trim()) return data;
        const q = searchQuery.toLowerCase();
        return data.filter((s) => s.name.toLowerCase().includes(q) || (s.target?.title ?? '').toLowerCase().includes(q));
    }, [schedules?.data, searchQuery]);

    const toggle = () => setIsOpen((prev) => !prev);

    return (
        <Container>
            <Row>
                <Col md={12}>
                    <h3 className="page-title">Schedules</h3>
                    <h3 className="page-subhead subhead">
                        Auto-rotate queued updates into Content or Course docs on a recurring cadence. Each schedule consumes one queue item per
                        fire.
                    </h3>
                </Col>
            </Row>
            <Row>
                <Col md={12} lg={12}>
                    <Card>
                        <CardBody>
                            {isLoading ? (
                                <div className="d-flex justify-content-center">
                                    <LoadingSpinner />
                                </div>
                            ) : (
                                <>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <MaterialTextField
                                            className="w-50"
                                            label="Search by Name or Target Title"
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        <MatTableToolbar onRequestAdd={toggle} />
                                    </div>
                                    <TableWrap>
                                        <Table>
                                            <MatTableHead
                                                order={order}
                                                orderBy={orderBy}
                                                onRequestSort={(_e: unknown, property: string) => {
                                                    let nextOrder: 'asc' | 'desc' = 'desc';
                                                    if (orderBy === property && order === 'desc') nextOrder = 'asc';
                                                    setOrder(nextOrder);
                                                    setOrderBy(property);
                                                }}
                                            />
                                            <TableBody>
                                                {filtered
                                                    .slice()
                                                    .sort(getSorting(order, orderBy))
                                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                    .map((s) => (
                                                        <TableRow
                                                            role="checkbox"
                                                            onClick={() => navigate(`/schedules/${s.id}`)}
                                                            tabIndex={-1}
                                                            key={s.id}
                                                        >
                                                            <TableCell component="th" scope="row" padding="none">
                                                                {s.name}
                                                            </TableCell>
                                                            <TableCell>{s.target?.type}</TableCell>
                                                            <TableCell>{s.target?.title ?? '—'}</TableCell>
                                                            <TableCell>{s.cadence}</TableCell>
                                                            <TableCell>
                                                                {String(s.publishAtHourUtc).padStart(2, '0')}:
                                                                {String(s.publishAtMinuteUtc).padStart(2, '0')}
                                                            </TableCell>
                                                            <TableCell>{s.nextPublishAt ? formatDate(s.nextPublishAt) : '—'}</TableCell>
                                                            <TableCell>{s.queueDepth ?? 0}</TableCell>
                                                            <TableCell>{s.enabled ? 'Yes' : 'No'}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                {filtered.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={8} className="text-center">
                                                            No schedules yet. Add one to get started.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableWrap>
                                    <TablePagination
                                        component="div"
                                        count={filtered.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        backIconButtonProps={{ 'aria-label': 'Previous Page' }}
                                        nextIconButtonProps={{ 'aria-label': 'Next Page' }}
                                        onPageChange={(_e: unknown, p: number) => setPage(p)}
                                        onRowsPerPageChange={(e: { target: { value: string } }) => {
                                            setRowsPerPage(Number(e.target.value));
                                            setPage(0);
                                        }}
                                        rowsPerPageOptions={[10, 25, 50]}
                                        dir="ltr"
                                        SelectProps={{ inputProps: { 'aria-label': 'rows per page' }, native: true }}
                                    />
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <ScheduleModal isOpen={isOpen} toggle={toggle} />
        </Container>
    );
};

export default Schedules;
